import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { BulkCandidatesWizard, type BulkWizardVariant } from './BulkCandidatesWizard.component';
import {
  isDeepDivePhase,
  phaseDirectionError,
  phaseOptionsFromJob,
  pushFatorH,
  runAdvance,
  runReject,
  type BulkClassification,
} from './bulk-flow';
import {
  downloadConflictsXlsx,
  downloadIdsTemplateXlsx,
  parseIdsXlsx,
  validateBulkFile,
  BulkIdsParseError,
  type ConflictRow,
  type ParsedIdsBase,
} from './bulk-xlsx';

/** RN-12 (EU-560) — Sonner de 8s com "Fechar". */
const SONNER_DURATION_MS = 8000;

const TOOLTIP_REQUIRED = 'Todos os campos são obrigatórios';
const TOOLTIP_FIX_ERRORS = 'Preencha os campos corretamente';

const COPY: Record<
  BulkWizardVariant,
  {
    title: string;
    confirmLabel: string;
    successToast: string;
    noEligible: string;
    alreadyReason: string;
    conflictsFile: string;
    routeFrom: '/vagas/$jobId/movimentar' | '/vagas/$jobId/reprovar';
  }
> = {
  move: {
    title: 'Movimentar candidatos',
    confirmLabel: 'Confirmar movimentação',
    successToast: 'Candidatos movimentados',
    noEligible: 'Base sem candidatos aptos para movimentação. Revise a base e envie novamente.',
    alreadyReason: 'Já movimentado',
    conflictsFile: 'conflitos-movimentacao.xlsx',
    routeFrom: '/vagas/$jobId/movimentar',
  },
  reject: {
    title: 'Reprovar candidatos',
    confirmLabel: 'Confirmar reprovação',
    successToast: 'Candidatos reprovados',
    noEligible: 'Base sem candidatos aptos para reprovação. Revise a base e envie novamente.',
    alreadyReason: 'Já reprovado',
    conflictsFile: 'conflitos-reprovacao.xlsx',
    routeFrom: '/vagas/$jobId/reprovar',
  },
};

/**
 * EU-551/EU-552 — container compartilhado dos wizards de movimentação e
 * reprovação em massa. Etapa 1 valida arquivo/campos localmente; "Avançar"
 * roda o DRY-RUN no Core (classificação tolerante) e só então abre a
 * Confirmação (RN-08/RN-04: base sem aptos pausa na Etapa 1). "Confirmar"
 * executa de fato, dispara o Fator H quando marcado (RN-05) e volta para a
 * Gestão de vaga com Sonner de 8s (RN-10/RN-06).
 */
export function BulkCandidatesWizardContainer({ variant }: { variant: BulkWizardVariant }) {
  const copy = COPY[variant];
  const navigate = useNavigate();
  const { jobId } = useParams({ from: copy.routeFrom });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const job = jobQuery.data;
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);
  const phases = useMemo(() => phaseOptionsFromJob(job), [job]);

  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedIdsBase | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [baseError, setBaseError] = useState<string | null>(null);
  const [originStepId, setOriginStepId] = useState('');
  const [destStepId, setDestStepId] = useState('');
  const [fatorHChecked, setFatorHChecked] = useState(false);
  const [classification, setClassification] = useState<BulkClassification | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const destOption = phases.find((p) => p.id === destStepId);
  const originOption = phases.find((p) => p.id === originStepId);
  const fatorHVisible = variant === 'move' && isDeepDivePhase(destOption);
  // QA 2026-07-30 — o disparo automático do Fator H na confirmação ainda não é
  // testável nos ambientes: o checkbox fica DESABILITADO (sempre "Não") e o
  // push nunca dispara por este fluxo. Reativar = flipar para false.
  const fatorHDisabled = true;

  // QA 2026-07-28 — movimentar para a MESMA fase é no-op de negócio: bloqueia
  // no formulário (erro inline no destino + Avançar desabilitado).
  const sameOriginDest =
    variant === 'move' && destStepId !== '' && originStepId !== '' && destStepId === originStepId;
  // RN Restrições de fase inicial/final (2026-07-30) — origem Trilha Online
  // não retrocede; origem Contratação não avança (o Core guarda com 422).
  const directionError = variant === 'move' ? phaseDirectionError(originOption, destOption) : null;
  const destError = sameOriginDest
    ? 'A fase de destino deve ser diferente da fase de origem'
    : directionError;

  const requiredFilled =
    parsed !== null && originStepId !== '' && (variant === 'reject' || destStepId !== '');
  const hasFormError = fileError !== null || destError !== null;
  const canAdvance = requiredFilled && !hasFormError && baseError === null;
  const advanceTooltip = hasFormError
    ? TOOLTIP_FIX_ERRORS
    : !requiredFilled
      ? TOOLTIP_REQUIRED
      : null;

  async function handleFileSelect(file: File | null) {
    setBaseError(null);
    setClassification(null);
    if (!file) return;
    const validation = validateBulkFile(file);
    if (validation) {
      setFileName(file.name);
      setParsed(null);
      setFileError(validation);
      return;
    }
    try {
      const ids = await parseIdsXlsx(file);
      setFileName(file.name);
      setParsed(ids);
      setFileError(null);
    } catch (err) {
      setFileName(file.name);
      setParsed(null);
      setFileError(
        err instanceof BulkIdsParseError ? err.message : 'Não foi possível ler o arquivo.'
      );
    }
  }

  /** Etapa 1 → dry-run → Etapa 2 (ou pausa com "base sem aptos", RN-08/RN-04). */
  async function handleAdvance() {
    if (!canAdvance || !parsed || isProcessing) return;
    // Base só com IDs malformados nem chega ao servidor.
    if (parsed.wellFormed.length === 0) {
      setBaseError(copy.noEligible);
      return;
    }
    setIsProcessing(true);
    try {
      const result =
        variant === 'move'
          ? await runAdvance({
              jobId,
              destStepId,
              // MOV-INC-05 — a origem é enviada e validada no backend.
              originStepId: originStepId || null,
              userIds: parsed.wellFormed,
              dryRun: true,
            })
          : await runReject({
              jobId,
              originStepId: originStepId || null,
              userIds: parsed.wellFormed,
              dryRun: true,
            });
      if (result.eligible.length === 0) {
        setBaseError(copy.noEligible);
        return;
      }
      setClassification(result);
      setStep('confirm');
    } catch {
      toast.error('Não foi possível processar a base. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }

  /** Confirmação → execução real (+ Fator H quando marcado) → Gestão de vaga. */
  async function handleConfirm() {
    if (!parsed || isProcessing) return;
    setIsProcessing(true);
    try {
      const result =
        variant === 'move'
          ? await runAdvance({
              jobId,
              destStepId,
              originStepId: originStepId || null,
              userIds: parsed.wellFormed,
              dryRun: false,
            })
          : await runReject({
              jobId,
              originStepId: originStepId || null,
              userIds: parsed.wellFormed,
              dryRun: false,
            });

      if (
        variant === 'move' &&
        fatorHVisible &&
        fatorHChecked &&
        !fatorHDisabled &&
        result.eligible.length > 0
      ) {
        try {
          await pushFatorH(jobId, result.eligible);
        } catch {
          // RN-05 — o disparo é best-effort: a movimentação já aconteceu.
          toast.error(
            'Candidatos movimentados, mas o disparo do Fator H falhou. Use o painel do Fator H para sincronizar.'
          );
        }
      }

      toast(copy.successToast, {
        duration: SONNER_DURATION_MS,
        action: { label: 'Fechar', onClick: () => undefined },
      });
      await navigate({ to: '/vagas/$jobId', params: { jobId } });
    } catch {
      toast.error('Não foi possível concluir a operação. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }

  function handleDownloadConflicts() {
    if (!classification || !parsed) return;
    const rows: ConflictRow[] = [
      ...classification.cancelled.map((c) => ({
        id: c.userId,
        name: c.name,
        email: c.email,
        reason: 'Desistiu da vaga',
      })),
      ...classification.alreadyProcessed.map((c) => ({
        id: c.userId,
        name: c.name,
        email: c.email,
        reason: copy.alreadyReason,
      })),
      // Reprovado não movimenta (decisão terminal do consultor — EU-552).
      ...classification.rejected.map((c) => ({
        id: c.userId,
        name: c.name,
        email: c.email,
        reason: 'Reprovado',
      })),
      // MOV-INC-05 — apto porém fora da "Fase de origem" selecionada.
      ...classification.notInOriginStep.map((c) => ({
        id: c.userId,
        name: c.name,
        email: c.email,
        reason: 'Fora da fase de origem',
      })),
      // IDs inválidos: só ID + motivo (RN-09 mov / RN-05 rep).
      ...classification.notSubscribed.map((id) => ({
        id,
        name: null,
        email: null,
        reason: 'ID inválido',
      })),
      ...parsed.malformed.map((id) => ({
        id,
        name: null,
        email: null,
        reason: 'ID inválido',
      })),
    ];
    void downloadConflictsXlsx(rows, copy.conflictsFile);
  }

  function handleBack() {
    if (step === 'confirm') {
      setStep('form');
      return;
    }
    void navigate({ to: '/vagas/$jobId', params: { jobId } });
  }

  if (jobQuery.isLoading) {
    return (
      <div
        data-testid={`job-${variant}-loading`}
        className="flex min-h-dvh items-center justify-center bg-card"
      >
        <div className="h-40 w-[420px] animate-pulse rounded-xl bg-muted" aria-hidden />
      </div>
    );
  }

  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
      return null;
    }
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-card">
        <p className="text-lg font-medium text-foreground">Não foi possível carregar a vaga</p>
        <button
          type="button"
          data-testid={`job-${variant}-error-retry`}
          onClick={() => void jobQuery.refetch()}
          className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const invalidCount =
    (classification?.notSubscribed.length ?? 0) + (parsed?.malformed.length ?? 0);

  return (
    <BulkCandidatesWizard
      variant={variant}
      title={copy.title}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      programName={job?.programName ?? ''}
      jobName={job?.name ?? ''}
      phases={phases}
      step={step}
      fileName={fileName}
      fileError={fileError}
      baseError={baseError}
      originStepId={originStepId}
      destStepId={destStepId}
      destError={destError}
      fatorHVisible={fatorHVisible}
      fatorHChecked={!fatorHDisabled && fatorHChecked}
      fatorHDisabled={fatorHDisabled}
      canAdvance={canAdvance}
      advanceTooltip={advanceTooltip}
      isProcessing={isProcessing}
      summary={
        classification
          ? {
              candidateCount: classification.eligible.length,
              originName: phases.find((p) => p.id === originStepId)?.name ?? null,
              destName: destOption?.name ?? null,
              ...(fatorHVisible ? { fatorH: !fatorHDisabled && fatorHChecked } : {}),
            }
          : null
      }
      conflicts={
        classification
          ? {
              cancelled: classification.cancelled.length,
              alreadyProcessed: classification.alreadyProcessed.length,
              rejected: classification.rejected.length,
              notInOrigin: classification.notInOriginStep.length,
              invalid: invalidCount,
            }
          : null
      }
      confirmLabel={copy.confirmLabel}
      onFileSelect={(file) => void handleFileSelect(file)}
      onDownloadTemplate={() => void downloadIdsTemplateXlsx()}
      onOriginChange={(id) => {
        setOriginStepId(id);
        setBaseError(null);
      }}
      onDestChange={(id) => {
        setDestStepId(id);
        setBaseError(null);
        setClassification(null);
      }}
      onFatorHChange={fatorHDisabled ? undefined : setFatorHChecked}
      onBack={handleBack}
      onAdvance={() => void handleAdvance()}
      onDownloadConflicts={handleDownloadConflicts}
      onConfirm={() => void handleConfirm()}
    />
  );
}
